import React, { useMemo, useState } from "react";
import "./ScannerTable.css";
import { toast } from "react-toastify";
import { Table } from "antd";
import { has } from "lodash";
import { uploadPetrifilmSingleCount, deletePetrifilmMapping } from "../../../../actions/micro";
import ScannerTableRow from "./ScannerTableRow";
import ScannerTableCell from "./ScannerTableCell";
import { getUser } from "../../../../utils/helpers";

const isTntcToggleOnForRow = (row) => {
  const token = String(row.counts ?? "").toLowerCase();
  return row.status === "TNTC" || token === "tntc";
};

function ScannerTable({
  samples,
  setSamples,
  setAllSamplesRechecked,
  setResolveHierarchyRecheckLoading,
  isResolvingHierarchy,
  migrateLoading,
  isTntcTable,
  isTntcScannerMode = false,
  onRowCountSubmitSuccess,
  onMappingRemoveSuccess,
  // handleReset,
}) {
  const [submittingKey, setSubmittingKey] = useState(-1);
  const [removingKey, setRemovingKey] = useState(-1);

  /**
   * Check if the row should be disabled
   * @param {Object} record sample object {key, wo_id, genista_id, df, petrifilm, status, mapping, recheck, counts, edit, submitting}
   * @returns {Boolean} true if should disable
   */
  const checkRowDisabled = (record) => !record.recheck && !record.edit && record.status !== "Pending" && record.status !== "Retest";

  /**
   * Check if the inputs and submit button should be disabled.
   * @returns {Boolean} true if should disable
   */
  const checkDisableInteraction = () => submittingKey > -1 || removingKey > -1 || migrateLoading;

  /**
   * Update sample in samples list
   * @param {Number} index index of sample in samples array
   * @param {Object} sample updated sample
   */
  const updateSample = (index, sample, afterSamplesUpdate) => {
    const samplesCopy = [...samples];
    samplesCopy[index] = sample;
    setSamples(samplesCopy);
    // if (samplesCopy.every((_sample) => checkDisabled(_sample))) { // autoclose modal if all samples are ready
    //   handleReset();
    // }
    /** If resolving hierarchy, check if all samples have been rechecked at least once. If so, enable submit button */
    if (isResolvingHierarchy) {
      setAllSamplesRechecked(samplesCopy.every(({ recheckedAtLeastOnce, edit }) => recheckedAtLeastOnce && !edit));
    }
    if (typeof afterSamplesUpdate === "function") {
      afterSamplesUpdate(samplesCopy, sample);
    }
  };

  const validateInputs = (sample, inputValues) => {
    const rawCount = inputValues?.counts;
    if (Number.isNaN(rawCount)) {
      setSubmittingKey(-1);
      toast.error("Raw count must be a number");
      return false;
    }

    // const { df } = sample;

    // let inputtedDf;
    // if (isPending && !df) {
    //   inputtedDf = inputValues?.df;
    //   if (Number.isNaN(inputtedDf)) {
    //     setSubmittingKey(-1);
    //     toast.error("DF must be a number");
    //     return false;
    //   }
    // }
    return true;
  };

  /**
   * Handle submit count (including recheck).
   * @param {Object} sample sample obj
   * @param {Object} inputValues {count, df}
   * @returns {void}
   */
  const handleSubmit = async (sample, inputValues) => {
    const index = sample.key;
    setSubmittingKey(index);
    if (isResolvingHierarchy) {
      setResolveHierarchyRecheckLoading(true);
    }
    let rawCount = sample.block ? Number(sample.counts) : inputValues?.counts;
    // raw count can be "tntc" only when it is from the TNTC scan, normal scan should not allow string in raw count
    if (sample.isTntcChecked && (isTntcTable || isTntcScannerMode)) {
      rawCount = "tntc";
    } else if (Number.isNaN(rawCount)) {
      setSubmittingKey(-1);
      toast.error("Raw count must be a number");
      return;
    }

    const {
      wo_id,
      genista_id,
      analysis,
      petrifilm,
      df,
      recheck,
      mapping,
      wo,
      sample_id,
      test_in_sample_id,
    } = sample;

    // let inputtedDf;
    // if (isPending && !df) {
    //   inputtedDf = inputValues?.df;
    //   if (Number.isNaN(inputtedDf)) {
    //     setSubmittingKey(-1);
    //     toast.error("DF must be a number");
    //     return;
    //   }
    // }

    const payload = {
      wo_id,
      genista_id,
      analysis,
      petrifilm,
      df,
      counts: String(rawCount),
      recheck,
      mapping,
      wo,
      sample_id,
      test_in_sample_id,
      user_id: getUser()?.id,
    };
    const { success, message, result } = await uploadPetrifilmSingleCount(payload);

    if (success) {
      toast.success("Successfully updated counts");
      const updatedSample = {
        ...sample,
        ...result,
        edit: false,
        recheck: isResolvingHierarchy ? false : result.recheck,
        recheckedAtLeastOnce: isResolvingHierarchy ? true : undefined,
      };

      // Explicitly handle qc_status
      if (result.qc_status === undefined) {
        updatedSample.qc_status = "-"; // Set to "-" if qc_status is not returned
      }

      updatedSample.isTntcChecked = isTntcToggleOnForRow(updatedSample);

      updateSample(index, updatedSample, onRowCountSubmitSuccess);
    } else {
      updateSample(index, { ...sample });
      toast.error(message);
    }
    setSubmittingKey(-1);
  };

  const createDeleteMappingPayload = (sample) => {
    const payload = { ...sample, user_id: getUser()?.id };
    const delete_keys = [
      "key",
      "edit",
    ];
    delete_keys.forEach(
      (key) => {
        if (has(payload, key)) {
          delete payload[key];
        }
      },
    );
    return payload;
  };

  /**
   * Handle removing analysis-petrifilm mapping
   * @param {*} sample
   * @param {*} form
   */
  const handleRemoveMapping = async (sample, form) => {
    const index = sample.key;
    setRemovingKey(index);
    const payload = createDeleteMappingPayload(sample);
    const { success, message, result } = await deletePetrifilmMapping(payload);
    if (success) {
      const next = { key: sample.key, ...result, edit: false };
      next.isTntcChecked = isTntcToggleOnForRow(next);
      updateSample(index, next);
      if (typeof onMappingRemoveSuccess === "function") {
        await onMappingRemoveSuccess();
      }
      /** reset the Count value */
      form.setFieldsValue({
        counts: undefined,
      });
    } else {
      toast.error(message);
    }
    setRemovingKey(-1);
  };

  // const STATUS_COLORS = { Pending: "#FF0000", Ready: "#008000", "": "#000000" };

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: "WO",
        dataIndex: "wo_id",
        key: "wo_id",
      },
      {
        title: "Genista ID",
        dataIndex: "genista_id",
        key: "genista_id",
      },
      {
        title: "Analysis",
        dataIndex: "analysis",
        key: "analysis",
      },
      {
        title: "Petrifilm",
        dataIndex: "petrifilm",
        key: "petrifilm",
      },
      {
        title: "DF",
        dataIndex: "df",
        key: "df",
      },
      {
        title: "Notes",
        dataIndex: "qc_status",
        key: "qc_status",
        render: (text) => text || "-", // Show "-" if qc_status is not present
      },
      {
        title: "Raw Count",
        dataIndex: "counts",
        key: "counts",
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        align: "center",
      },
    ];
    if (!isResolvingHierarchy) {
      columns.push({
        title: "Mapping",
        dataIndex: "mapping",
        key: "mapping",
        align: "center",
      });
    }
    /** Multi-row TNTC popup or single-row TNTC scan — column must show so toggle is available when count input is TNTC-gated. */
    if (isTntcTable || isTntcScannerMode) {
      columns.splice(6, 0, {
        title: "TNTC",
        dataIndex: "tntc",
        key: "tntc",
        align: "center",
      });
    }
    return columns;
  }, [isResolvingHierarchy, isTntcTable, isTntcScannerMode]);

  const expandRowText = () => "Verify raw count for the above row";

  return (
    <Table
      components={{
        body: {
          row: ScannerTableRow,
          cell: ScannerTableCell,
        },
      }}
      onRow={(record, index) => (
        { record, index, checkRowDisabled }
      )}
      expandable={{
        showExpandColumn: false,
        rowExpandable: (record) => record.recheck,
        defaultExpandAllRows: true,
        expandedRowRender: () => <span className="ScannerTable__ExpandedCell">{expandRowText()}</span>,
      }}
      sticky
      tableLayout="fixed"
      columns={tableColumns.map((col) => ({
        ...col,
        onCell: (record) => ({
          record,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSubmit,
          handleRemoveMapping,
          updateSample,
          checkRowDisabled,
          checkDisableInteraction,
          validateInputs,
          submittingKey,
          removingKey,
          isResolvingHierarchy,
          isTntcTable,
          isTntcScannerMode,
        }),
      }))}
      dataSource={samples}
      pagination={false}
      bordered
      className="ScannerTable"
    />
  );
}

export default ScannerTable;
